import { toast } from 'sonner';
import type { ChangeDecision, DiffStatus, UIMessage } from '@/types/chat';
import { useChatStore } from '@/stores/chatStore';
import { applyChanges, revertAppliedChanges, isCellEditingModeError, isInOffice, type ApplyOptions } from '@/services/office';
import { confirmChanges } from '@/services/chat';
import { extractErrorMessage } from '@/services/api';

export interface ApplyDiffResult {
  ok: boolean;
  appliedIds: string[];
  failed: { id: string; reason: string }[];
}

// Hard deadline on one applyChanges run. Office.js can wedge on a sync
// (host busy, modal dialog, document lock) with no error and no way to
// cancel the underlying Excel.run — without a deadline the applying bar
// spins forever. On timeout the diff resolves to 'partial': some writes
// may have landed before the wedge, so neither 'applied' nor 'rejected'
// would be honest.
const APPLY_TIMEOUT_MS = 90_000;

// applyDiffMessage is the SINGLE writer path for a proposed diff. Used by
// the manual Apply button (DiffPreview / PendingDiffBar) AND the auto-build
// orchestrator, so the apply logic, audit, status transitions, and toasts
// live in exactly one place.
//
// Double-apply guard: it reads the LIVE diff status from the store and bails
// unless it is exactly 'pending', then atomically flips to 'applying'. A
// second concurrent caller therefore no-ops.
export async function applyDiffMessage(
  conversationId: string | null,
  message: UIMessage,
  opts?: ApplyOptions,
): Promise<ApplyDiffResult> {
  const store = useChatStore.getState();
  // Re-read the live message so a stale snapshot can't bypass the guard.
  const live = store.messages.find((m) => m.id === message.id) ?? message;
  const diff = live.diff;
  if (!diff || diff.status !== 'pending') {
    return { ok: false, appliedIds: [], failed: [] };
  }
  if (!conversationId) {
    toast.error('Conversation not saved yet; cannot apply.');
    return { ok: false, appliedIds: [], failed: [] };
  }
  // Environment auto-detect: applying writes through Office.js, which only
  // exists inside the Excel host. In a plain browser tab (the website) there
  // is no live workbook, so don't fire applyChanges (it would fail every
  // change with "Not running in Excel"). Show one clear message instead.
  if (!isInOffice()) {
    toast.error('Preview only — open Excel Autopilot inside Excel to apply these changes to your workbook.');
    return { ok: false, appliedIds: [], failed: [] };
  }

  const changes = diff.changes;
  const selected = diff.selected;
  const toApply = changes.filter((c) => selected[c.id]);
  if (toApply.length === 0) {
    return { ok: true, appliedIds: [], failed: [] };
  }

  // Capture total BEFORE setDiffStatus(..., appliedIds) trims diff.changes,
  // so a "done of total" denominator stays stable.
  store.setDiffStatus(message.id, 'applying'); // leaves 'pending' atomically

  try {
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<'timeout'>((resolve) => {
      timeoutHandle = setTimeout(() => resolve('timeout'), APPLY_TIMEOUT_MS);
    });
    const raced = await Promise.race([applyChanges(toApply, opts), timeout]);
    clearTimeout(timeoutHandle);
    if (raced === 'timeout') {
      store.setDiffStatus(message.id, 'partial');
      toast.error('Apply timed out — some changes may not have applied. Check the sheet.');
      return { ok: false, appliedIds: [], failed: [{ id: 'all', reason: 'apply timed out' }] };
    }
    const { appliedIds, failed } = raced;
    const failReason = new Map(failed.map((f) => [f.id, f.reason]));

    const decisions: ChangeDecision[] = changes.map((c) => {
      if (!selected[c.id]) return { change_id: c.id, outcome: 'rejected' };
      if (appliedIds.includes(c.id)) return { change_id: c.id, outcome: 'approved' };
      // User approved it but Excel rejected the write. Report outcome
      // 'failed' WITH the reason + op + target so the next model turn can
      // diagnose and fix the cause instead of silently re-proposing it.
      return {
        change_id: c.id,
        outcome: 'failed',
        failure_reason: failReason.get(c.id) ?? 'apply failed',
        op: c.type,
        target: `${c.sheet}!${c.cell ?? c.range ?? ''}`.replace(/!$/, ''),
      };
    });

    try {
      await confirmChanges({ conversation_id: conversationId, message_id: message.id, decisions });
    } catch (err) {
      // Audit failure is NON-FATAL — never sets ok:false (doesn't touch the
      // workbook, so it must not stop an auto-build chain).
      console.warn('confirmChanges failed:', extractErrorMessage(err));
    }

    const finalStatus: DiffStatus =
      failed.length === 0 ? 'applied' : appliedIds.length === 0 ? 'rejected' : 'partial';
    store.setDiffStatus(message.id, finalStatus, appliedIds);

    if (failed.length > 0) {
      const first = failed[0];
      if (isCellEditingModeError(first.reason)) {
        toast.error('Excel cell edit mode me hai. Koi empty cell pe click karo (ya ENTER/TAB press karo), phir Apply dobara click karo.');
      } else {
        const reason = first.reason ? ` — ${first.reason}` : '';
        toast.error(`${failed.length} change(s) could not be applied${reason}`);
      }
    } else {
      toast.success(`Applied ${appliedIds.length} change(s)`);
    }

    return { ok: failed.length === 0, appliedIds, failed };
  } catch (err) {
    store.setDiffStatus(message.id, 'rejected');
    toast.error(extractErrorMessage(err, 'Apply failed'));
    return { ok: false, appliedIds: [], failed: [{ id: 'all', reason: 'apply threw' }] };
  }
}

// revertDiffMessage undoes an already-applied diff, restoring the captured
// before-state for the reversible change types. Structural / object / bulk
// ops have no safe inverse and are reported so the user can fall back to
// Excel's own undo for those. On success the diff resolves to 'rejected'
// so the card stops offering Apply.
export async function revertDiffMessage(message: UIMessage): Promise<void> {
  const store = useChatStore.getState();
  const live = store.messages.find((m) => m.id === message.id) ?? message;
  const diff = live.diff;
  if (!diff || (diff.status !== 'applied' && diff.status !== 'partial')) return;
  if (!isInOffice()) {
    toast.error('Open Excel Autopilot inside Excel to revert changes.');
    return;
  }

  // setDiffStatus('applied'/'partial', appliedIds) already trimmed
  // diff.changes to the changes that actually applied, so they're exactly
  // the revert candidates.
  try {
    const { revertedIds, unrevertible } = await revertAppliedChanges(diff.changes);
    store.setDiffStatus(message.id, 'rejected', []);
    if (revertedIds.length > 0 && unrevertible.length === 0) {
      toast.success(`Reverted ${revertedIds.length} change(s)`);
    } else if (revertedIds.length > 0) {
      toast.success(`Reverted ${revertedIds.length}; ${unrevertible.length} need Excel undo (Ctrl+Z)`);
    } else {
      toast.error('These changes can’t be auto-reverted — use Excel undo (Ctrl+Z).');
    }
  } catch (err) {
    toast.error(extractErrorMessage(err, 'Revert failed'));
  }
}
